import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatCents, formatDate } from "@/lib/api";
import { Heart, ArrowRight, Clock, CheckCircle2, AlertCircle, Send } from "lucide-react";

/**
 * Retention pulse card — surfaced on the landlord Dashboard above the
 * recent-properties grid. Shows the 3 most time-sensitive leases plus a
 * "lock in dependable rent" pitch + ring KPI for stable tenants.
 *
 * Hits GET /api/retention/overview (landlord-only). Silently renders
 * nothing if backend not ready or there are 0 active leases.
 */
export default function RetentionPulseCard() {
  const [d, setD] = useState(null);

  useEffect(() => {
    let m = true;
    api.get("/retention/overview")
      .then((r) => { if (m) setD(r.data); })
      .catch(() => {});
    return () => { m = false; };
  }, []);

  if (!d || (d.kpis?.total_leases || 0) === 0) return null;
  const k = d.kpis || {};
  // Surface only leases that need a decision soon, capped at 3.
  const focus = (d.rows || [])
    .filter((r) => r.bucket === "renew_now" || r.bucket === "renew_soon" || r.bucket === "expired")
    .slice(0, 3);

  const stablePct = k.total_leases ? Math.round((k.stable / k.total_leases) * 100) : 0;

  return (
    <section className="mt-6 grid lg:grid-cols-3 gap-5" data-testid="retention-pulse-card">
      {/* Pitch + KPIs */}
      <div className="rounded-2xl p-6 bg-gradient-to-br from-emerald-600 via-teal-600 to-emerald-700 text-white shadow-lg shadow-emerald-500/20 relative overflow-hidden">
        <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-white/10 blur-2xl" />
        <div className="absolute -right-5 bottom-0 w-32 h-32 rounded-full bg-sky-400/30 blur-3xl" />
        <div className="relative">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/15 backdrop-blur text-[10px] uppercase tracking-[0.18em] font-bold">
            <Heart className="w-3 h-3" /> Tenant retention
          </div>
          <h3 className="font-display text-xl font-extrabold mt-3 leading-tight">
            Long-term tenants. Reliable cash flow.
          </h3>
          <p className="text-sm text-emerald-50/90 mt-2 leading-relaxed">
            The cheapest tenant you&apos;ll ever sign is the one already living there. Lock in renewals before they think about leaving.
          </p>

          <div className="mt-5 grid grid-cols-3 gap-3">
            <MiniKpi label="Stable" value={`${stablePct}%`} testid="retention-pulse-stable" />
            <MiniKpi label="Renew now" value={k.renew_now ?? 0} testid="retention-pulse-renew-now" />
            <MiniKpi label="Avg tenure" value={`${k.avg_tenure_months ?? 0}m`} testid="retention-pulse-tenure" />
          </div>

          <Link to="/retention" data-testid="retention-pulse-cta"
            className="mt-5 inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-white text-emerald-700 font-semibold text-sm hover:bg-emerald-50 transition-colors">
            View retention board <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>

      {/* Focus list */}
      <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 p-6 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-display text-base font-bold text-slate-900">Leases needing a decision</h3>
            <p className="text-xs text-slate-500 mt-0.5">Sorted by urgency · 60-day horizon</p>
          </div>
          <Link to="/retention" className="text-sm font-semibold text-emerald-700 hover:underline" data-testid="retention-pulse-seeall">
            See all →
          </Link>
        </div>

        {focus.length === 0 ? (
          <div className="text-center py-8" data-testid="retention-pulse-empty">
            <CheckCircle2 className="w-9 h-9 mx-auto text-emerald-500 mb-2" />
            <div className="text-sm font-semibold text-slate-900">All leases are on solid footing</div>
            <p className="text-xs text-slate-500 mt-1">No expirations in the next 60 days.</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {focus.map((r) => (
              <FocusRow key={r.lease_id} r={r} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function MiniKpi({ label, value, testid }) {
  return (
    <div data-testid={testid} className="rounded-lg bg-white/10 backdrop-blur px-3 py-2.5 ring-1 ring-white/15">
      <div className="text-[10px] uppercase tracking-[0.16em] font-bold opacity-80">{label}</div>
      <div className="text-xl font-bold mt-0.5 tabular-nums">{value}</div>
    </div>
  );
}

function FocusRow({ r }) {
  const isExpired = r.bucket === "expired";
  const days = r.days_to_lease_end;
  const action = r.suggested_action || {};
  return (
    <Link
      to={action.route || "/retention"}
      data-testid={`retention-pulse-row-${r.lease_id}`}
      className="flex items-center gap-3 py-3 hover:bg-slate-50 -mx-2 px-2 rounded-lg transition-colors"
    >
      <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-none ${
        isExpired ? "bg-rose-100 text-rose-700" : days <= 30 ? "bg-amber-100 text-amber-700" : "bg-sky-100 text-sky-700"
      }`}>
        {isExpired ? <AlertCircle className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-sm text-slate-900 truncate">{r.tenant_name || r.tenant_email || "Tenant"}</div>
        <div className="text-xs text-slate-500 truncate">
          {r.property_name || "Property"} · {formatCents(r.monthly_rent_cents)}/mo · ends {formatDate(r.end_date)}
        </div>
      </div>
      <div className="text-right flex-none">
        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">
          {isExpired ? `${Math.abs(days)}d overdue` : `${days}d left`}
        </div>
        <div className="inline-flex items-center gap-1 mt-1 text-xs font-semibold text-emerald-700">
          <Send className="w-3 h-3" /> {action.label || "Open"}
        </div>
      </div>
    </Link>
  );
}
